import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// Per-agent tool (plugin) grants — writes ai.AgentPlugin, the table
// AgentRepository.GetPlugins resolves the model's tool list from (since the
// 2026-07-21 migration off the ai.AiCapabilityPlugin capability chain).
//
// Grants are keyed on plugin NAME — the stable LLM-boundary identifier the
// catalog already carries on every row — not on the numeric id. Grant and
// revoke are idempotent server-side, so the UI can fire on every toggle
// without first checking the current state.
//
// Backend: RestApi.Tenant AgentPluginController.

/** The ACTIVE plugin names granted to an agent — same source the model's tool list uses. */
export async function listAgentPluginNames(agentId: number): Promise<string[]> {
  const wrapped = await apiGet<OperationResult<string[]>>(`/api/AgentPlugin/ForAgent/${agentId}`);
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to load the agent’s tools');
  }
  return wrapped.data ?? [];
}

/**
 * Grant one tool to an agent. Idempotent: re-granting is a no-op and a previously
 * revoked grant is reactivated. The backend rejects an unknown plugin name with
 * isSuccess=false (not an HTTP error), which we surface verbatim.
 */
export async function grantPluginToAgent(agentId: number, pluginName: string): Promise<void> {
  const wrapped = await apiPost<OperationResult<unknown>>('/api/AgentPlugin/Grant', {
    agentId,
    pluginName,
  });
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || `Failed to grant "${pluginName}" to the agent`);
  }
}

/**
 * Revoke one tool from an agent. Flips the grant's IsActive rather than deleting,
 * so the audit trail survives and re-granting restores it. Revoking a tool the
 * agent doesn't have is a quiet success.
 */
export async function revokePluginFromAgent(agentId: number, pluginName: string): Promise<void> {
  const wrapped = await apiPost<OperationResult<unknown>>('/api/AgentPlugin/Revoke', {
    agentId,
    pluginName,
  });
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || `Failed to revoke "${pluginName}" from the agent`);
  }
}
